import {setElementInnerHTML} from '../../ui_utils/DomUtils'
import {ItemData, UIComponentItemPanelCell} from './UIComponentItemPanelCell'

export class UIComponentItemPanelGrid {
    private readonly container: HTMLDivElement
    private readonly scrollBoxContainer: HTMLDivElement
    private readonly scrollBox: HTMLDivElement
    private readonly unlockedItemsContainer: HTMLDivElement
    private readonly lockedItemsContainer: HTMLDivElement
    private readonly divider: HTMLDivElement
    private readonly upArrow: HTMLImageElement
    private readonly downArrow: HTMLImageElement

    private readonly items: UIComponentItemPanelCell[] = []

    private isScrolledToBottom: boolean

    constructor(container: HTMLDivElement) {
        this.container = container
        this.scrollBoxContainer = container.querySelector('.item-panel-scrollbox-container') as HTMLDivElement
        this.scrollBox = container.querySelector('.item-panel-grid-scrollbox') as HTMLDivElement
        this.unlockedItemsContainer = container.querySelector('#unlocked-items') as HTMLDivElement
        this.lockedItemsContainer = container.querySelector('#locked-items') as HTMLDivElement
        this.divider = container.querySelector('.grid-divider') as HTMLDivElement
        this.upArrow = container.querySelector('.up-arrow') as HTMLImageElement
        this.downArrow = container.querySelector('.down-arrow') as HTMLImageElement
        this.initScroll()
    }

    addItem(item: ItemData) {
        const itemContainer = item.available ? this.unlockedItemsContainer : this.lockedItemsContainer
        if(item.available == false) this.unhideDivider()
        this.items.push(new UIComponentItemPanelCell(itemContainer, item))
    }

    update() {
        if(this.shouldResetScrollView()) this.setScrolledToTopView()
        this.setDownArrowVisibility()
    }

    updateInteractabilityOfElements(isGuest: boolean) {
        this.items.forEach((item: UIComponentItemPanelCell) => {
            item.updateInteractability(isGuest)
        })
    }

    clear() {
        this.items.length = 0
        setElementInnerHTML(this.unlockedItemsContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        setElementInnerHTML(this.lockedItemsContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        this.hideDivider()
    }

    resetToTop() {
        this.setScrolledToTopView()
        this.setDownArrowVisibility()
    }

    private initScroll() {
        this.upArrow.onclick = () => {
            this.scrollUp()
            this.setDownArrowVisibility()
        }
        this.downArrow.onclick = () => {
            this.scrollDown()
            this.setDownArrowVisibility()
        }
        this.setScrolledToTopView()
    }

    private scrollUp() {
        const scrollAmount = this.container.clientHeight * .5
        const newPosition = this.scrollBox.offsetTop + scrollAmount
        this.scrollBox.style.top = newPosition + 'px'

        if(this.shouldScrollToTop(newPosition)) {
            this.setScrolledToTopView()
            return
        }
        this.setScrolledToMiddleView()
    }

    private scrollDown() {
        const scrollAmount = this.container.clientHeight * .5
        const newPosition = this.scrollBox.offsetTop - scrollAmount
        this.scrollBox.style.top = newPosition + 'px'

        const nextPosition = newPosition - scrollAmount
        if(this.shouldScrollToBottom(nextPosition)) {
            this.setScrolledToBottomView()
            return
        }
        this.setScrolledToMiddleView()
    }

    // eslint-disable-next-line class-methods-use-this
    private shouldScrollToTop(topPosition: number): boolean {
        return topPosition > 0
    }

    private shouldScrollToBottom(topPosition: number): boolean {
        if(this.isScrolledToBottom) return true
        if(this.isContentShorterThanContainer()) return false
        const amountScrolled = Math.abs(topPosition)
        const scrollRemaining = this.scrollBox.scrollHeight - amountScrolled
        return scrollRemaining <= this.scrollBoxContainer.clientHeight
    }

    private shouldResetScrollView(): boolean {
        if(this.isContentShorterThanContainer()) return true
        const scrolledOutOfView = Math.abs(this.scrollBox.offsetTop) > this.scrollBox.scrollHeight
        return scrolledOutOfView
    }

    private setScrolledToTopView() {
        this.scrollBoxContainer.classList.remove('short-view')
        this.scrollBoxContainer.classList.remove('up-arrow-shown')
        this.scrollBox.style.top = '0px'
        this.upArrow.hidden = true
        this.isScrolledToBottom = false
    }

    private setScrolledToMiddleView() {
        this.scrollBoxContainer.classList.add('short-view')
        this.scrollBoxContainer.classList.add('up-arrow-shown')
        this.upArrow.hidden = false
        this.isScrolledToBottom = false
    }

    private setScrolledToBottomView() {
        this.scrollBoxContainer.classList.add('short-view')
        this.scrollBoxContainer.classList.add('up-arrow-shown')
        this.scrollBox.style.top = (this.scrollBoxContainer.clientHeight - this.scrollBox.offsetHeight) + 'px'
        this.upArrow.hidden = false
        this.isScrolledToBottom = true
    }

    private hideDivider() {
        this.divider.hidden = true
    }

    private unhideDivider() {
        this.divider.hidden = false
    }

    private isContentShorterThanContainer() {
        return this.scrollBox.scrollHeight < this.scrollBoxContainer.clientHeight
    }

    private setDownArrowVisibility() {
        const shouldHideDownArrow = this.isContentShorterThanContainer() || this.isScrolledToBottom
        this.downArrow.hidden = shouldHideDownArrow
    }
}
